import {
  type UniformShadeCharacteristics,
  type ShadeCharacteristics,
  isPartialMultiShadeCharacteristics,
  type DeepPartial,
} from "@jasper/client-models";
import type { ValidationErrors } from "../../types";
import {
  Box,
  Group,
  SegmentedControl,
  Select,
  Stack,
  Text,
} from "@mantine/core";
import { IconPalette } from "@tabler/icons-react";
import { ButtonToggle } from "../../components";
import { MultiShadeFormV2 } from "./MultiShadeFormV2";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { normalizeShadeCharacteristics } from "./utils";
import type { ShadeCharacteristicsFormProps } from "./ShadeCharacteristicsForm";

export const ShadeCharacteristicsFormV2 = ({
  value,
  errors,
  onChange,
  itemShadeTypes,
  itemShades,
}: ShadeCharacteristicsFormProps) => {
  const { t } = useTranslation("forms");

  const selectableShade = useMemo(
    () => itemShades.filter(({ shadeType }) => shadeType === value?.shadeType),
    [itemShades, value?.shadeType]
  );

  const handleSubmit = (next: DeepPartial<ShadeCharacteristics>) =>
    onChange(normalizeShadeCharacteristics({ value: next, itemShades }));

  return (
    <Stack gap="md">
      <Stack gap="s1">
        <Group>
          <Group gap="s1">
            <IconPalette size={14} color="var(--mantine-color-brand-5)" />
            <Text size="xs" tt="uppercase" fw={400}>
              {t("forms.shadeCharacteristics.labels.shadeType")}
            </Text>
          </Group>
          <SegmentedControl
            // `""` (not `undefined`) keeps the control controlled, so no shade
            // type looks selected until one is actually set.
            value={value?.shadeType ?? ""}
            onChange={shadeType =>
              // Shades are filtered by shade type, so reset the selected shade(s)
              // whenever the shade type changes.
              handleSubmit({
                ...value,
                shadeType,
                shade: undefined,
                baseShade: undefined,
                incisalShade: undefined,
                gingivalShade: undefined,
              })
            }
            data={itemShadeTypes}
            bd={
              errors?.shadeType
                ? "1px solid var(--mantine-color-error)"
                : undefined
            }
          />
        </Group>
        {errors?.shadeType && (
          <Text size="sm" c="error">
            {t("forms.shadeCharacteristics.errors.shadeType")}
          </Text>
        )}
      </Stack>

      <Group align="flex-start">
        <Box>
          {value && isPartialMultiShadeCharacteristics(value) ? (
            <MultiShadeFormV2
              disabled={!value || !value.shadeType}
              value={value}
              errors={errors}
              onChange={multiShade => handleSubmit({ ...value, ...multiShade })}
              itemShades={selectableShade}
            />
          ) : (
            <Select
              label={
                <Text size="xs" tt="uppercase" fw={600} c="ink">
                  {t("forms.shadeCharacteristics.labels.shade")}
                </Text>
              }
              // `null` (not `undefined`) keeps the Select controlled so it
              // clears when the shade is reset (e.g. on shade type change).
              value={value?.shade ?? null}
              data={selectableShade}
              name="Shade"
              placeholder={t("forms.shadeCharacteristics.placeholders.shade")}
              disabled={!value || !value.shadeType}
              onChange={shade =>
                handleSubmit({ ...value, shade: shade ?? undefined })
              }
              error={
                (errors as ValidationErrors<UniformShadeCharacteristics>)?.shade
                  ? t("forms.shadeCharacteristics.errors.shade")
                  : undefined
              }
            />
          )}
        </Box>

        <Stack gap="s2">
          <Text size="xs" tt="uppercase" fw={600} c="ink">
            {t("forms.shadeCharacteristics.labels.isMultiShade")}
          </Text>
          <ButtonToggle
            value={value?.isMultiShade}
            onChange={isMultiShade => handleSubmit({ ...value, isMultiShade })}
            trueLabel={t("common.yes")}
            falseLabel={t("common.no")}
          />
        </Stack>
      </Group>
    </Stack>
  );
};
